import { getCircleHitArea } from '../../../elements/circle';
import {
  type ISize,
  type Point,
  mergeHitAreas,
  moveHitArea,
  point,
  segment,
} from '../../../elements/index';
import { getRectangleHitArea } from '../../../elements/rectangle';
import { wrapInSvgTag } from '../../../svg/wrapInSvgTag';
import { squared } from '../../../utils/numbers';
import {
  type NodeIconOptions,
  type NodeLabelStyle,
  type NodeTypeIcon,
  nodeIconDefaultOptions,
} from '../NodeTypeIcon';
import { getCloudyColors, makeGetCloudyClippingPoint } from '../cloudyCommon';

const HEIGHT = 60;
const WIDTH = 100;

const circles: [center: Point, radius: number][] = [
  [point.create(-17.1, 14.1), 15.8], // left
  [point.create(7.1, 3.8), 24.5], // center
  [point.create(28.5, 11.3), 18.5], // right
  [point.create(-24.7, -7.7), 22.3], // globe
];

const cloudBottomSegment = segment.createNonZero(
  { x: -18, y: 30 },
  { x: 29, y: 30 },
);

const createTransitIcon = (options: NodeIconOptions = {}): string => {
  const opts = { ...nodeIconDefaultOptions, ...options };

  const { fill, stroke } = getCloudyColors(opts.style);

  const icon = `<path fill="${fill}" d="M17.2135 41.7533C9.4577 38.6325 3.97913 31.0064 3.97913 22.0936C3.97913 10.4033 13.4045 0.926361 25.0312 0.926361C33.4547 0.926361 40.7227 5.90068 44.0881 13.0872C47.8615 10.7046 52.326 9.32677 57.1106 9.32677C66.6098 9.32677 74.8474 14.7578 78.9195 22.7002C88.941 22.9123 97 31.1469 97 41.274C97 51.126 89.373 59.1868 79.7319 59.8129V59.8518H78.6111L78.523 59.852L78.435 59.8518H32.8281L32.7624 59.8519C24.0838 59.8519 17.0484 52.778 17.0484 44.0519C17.0484 43.2712 17.1047 42.5037 17.2135 41.7533Z"/>
<path fill="${stroke}" fill-rule="evenodd" clip-rule="evenodd" d="M26.2925 0.0235738C26.339 0.0255078 26.4112 0.0345568 26.4112 0.0345568C34.6264 0.491837 41.6486 5.44705 45.0825 12.4918C43.9137 13.1546 42.8043 13.911 41.7646 14.7509H35.4112C35.8874 16.7836 36.1394 18.9034 36.1394 21.0822C36.1394 21.1209 36.1393 21.1596 36.1392 21.1983C34.8348 23.3569 33.8509 25.7329 33.2536 28.2596C33.2242 28.2586 33.1948 28.2578 33.1653 28.2571C33.5792 26.8323 33.8729 25.3561 34.0347 23.8402H16.3303C16.4373 26.3217 16.8959 28.7115 17.6574 30.9604H23.9618C23.1121 31.539 22.3217 32.1991 21.6017 32.9295H18.4164C18.7796 33.7665 19.186 34.5801 19.6329 35.3677C19.2201 35.9973 18.8509 36.6584 18.5297 37.3465C17.659 35.9532 16.9089 34.4759 16.2942 32.9295H7.9484C10.149 36.5214 13.4303 39.3725 17.3366 41.0247C17.2092 41.6846 17.1227 42.3591 17.0798 43.0456C8.83689 39.7967 3 31.7275 3 22.2876C3 9.97849 12.9242 0 25.1663 0C25.5111 0 25.854 0.00791373 26.1949 0.0235704C26.1949 0.0235704 26.2545 0.0219898 26.2925 0.0235738ZM7.30162 12.7818C10.4715 6.77157 16.5538 2.55211 23.6573 2.02484C20.3747 4.92782 17.788 8.60902 16.1829 12.7818H7.30162ZM6.39414 14.7509H15.5101C14.8311 17.0151 14.4341 19.4023 14.3603 21.8711H4.96249C5.01272 19.3594 5.51623 16.9596 6.39414 14.7509ZM17.563 14.7509C16.8299 17.0028 16.3994 19.3924 16.3196 21.8711H34.1692C34.1771 21.6091 34.1811 21.3461 34.1811 21.0822C34.1811 18.8968 33.9082 16.7755 33.3948 14.7509H17.563ZM5.01645 23.8402H14.3703C14.4692 26.3113 14.892 28.6986 15.5971 30.9604H6.88647C5.8581 28.773 5.20666 26.3713 5.01645 23.8402ZM43.0311 12.7818H34.8719C33.6271 8.82194 31.5155 5.24934 28.7617 2.28968C34.9594 3.40871 40.1763 7.36905 43.0311 12.7818ZM26.1852 2.47866C29.1579 5.29206 31.4557 8.81655 32.8099 12.7818H18.2951C20.0081 8.69851 22.7457 5.15571 26.1852 2.47866Z" />
<path fill="${stroke}" fill-rule="evenodd" clip-rule="evenodd" d="M78.4442 57.8828L78.5231 57.883L78.6019 57.8828L78.6111 57.8827L79.0694 57.8828L79.6057 57.8479C88.2231 57.2883 95.0417 50.081 95.0417 41.274C95.0417 32.2205 87.8368 24.8584 78.8784 24.6688L77.713 24.6442L77.1789 23.6024C73.4295 16.2895 65.8484 11.2958 57.1106 11.2958C46.455 11.2958 37.5202 18.7248 35.1589 28.7148L34.7897 30.2769L33.1936 30.2277C33.0505 30.2233 32.9068 30.221 32.7625 30.221C25.1654 30.221 19.0068 36.4133 19.0068 44.052C19.0068 51.6906 25.1654 57.8829 32.7625 57.8829L32.8202 57.8828L32.8282 57.8827L78.4442 57.8828ZM79.732 59.8129V59.8518H78.6111L78.5231 59.852L78.435 59.8518H32.8282L32.7625 59.8519C24.0839 59.8519 17.0485 52.778 17.0485 44.052C17.0485 35.3259 24.0839 28.252 32.7625 28.252C32.9268 28.252 33.0905 28.2545 33.2537 28.2596C35.8199 17.4024 45.5273 9.32678 57.1106 9.32678C66.6098 9.32678 74.8475 14.7579 78.9196 22.7002C88.941 22.9123 97 31.1469 97 41.274C97 51.126 89.373 59.1868 79.732 59.8129Z" />
`;

  return wrapInSvgTag(icon, {
    width: Math.round(WIDTH * opts.scale),
    height: Math.round(HEIGHT * opts.scale),
    viewBox: '0 0 100 60',
    ...opts.attributes,
  });
};

const getTransitClippingPoint = makeGetCloudyClippingPoint({
  circles,
  segments: [cloudBottomSegment],
});

const transitIconLabelStyle: NodeLabelStyle = {
  anchor: { x: 0.5, y: 0.5 },
  offset: { x: 5, y: 12 },
};

const transitIconSize: ISize = { width: WIDTH, height: HEIGHT };

// approximately rectangular, not worth exact calculation
const TRANSIT_ICON_MAX_RADIUS = Math.sqrt(
  squared(WIDTH / 2) + squared(HEIGHT / 2),
);

const transitHitArea = mergeHitAreas(
  moveHitArea(getRectangleHitArea(48, 30), { x: 6, y: 15 }),
  ...circles.map(([center, radius]) =>
    moveHitArea(getCircleHitArea(radius), center),
  ),
);

export const transitIcon: NodeTypeIcon = {
  createIconSvg: createTransitIcon,
  makeGetClippingPoint: () => getTransitClippingPoint,
  getHitArea: () => transitHitArea,
  getLabelStyle: () => transitIconLabelStyle,
  getMaxRadius: () => TRANSIT_ICON_MAX_RADIUS,
  getSize: () => transitIconSize,
  shape: 'transit',
};
